import type { NdisCategoryManagementKind } from '../funder';
import { isNdisCategoryManagementKind, isNdisManagementKind } from '../funder';

export type NdisBillingPayerKind = 'PLAN_MANAGER' | 'CLIENT' | 'NDIA_AGENCY' | 'UNRESOLVED';

export type NdisBillingRoute = {
  payerKind: NdisBillingPayerKind;
  managementKindApplied: NdisCategoryManagementKind | 'UNRESOLVED';
  skipReason: string | null;
  /** Agency bulk payment request export required (not automated submit). */
  requiresAgencyCsvExport: boolean;
};

/**
 * Resolve who pays for a delivery under NDIS plan management rules.
 * MIXED requires supportCategory + overrides; otherwise fail-closed.
 */
export function resolveNdisBillingRoute(input: {
  planManagementKind: string | null | undefined;
  defaultManagementKind?: string | null | undefined;
  categoryOverrides?: readonly { supportCategory: string; managementKind: string }[] | null;
  supportCategory?: string | null;
}): NdisBillingRoute {
  const planKind = String(input.planManagementKind ?? '').trim();
  if (!isNdisManagementKind(planKind)) {
    return {
      payerKind: 'UNRESOLVED',
      managementKindApplied: 'UNRESOLVED',
      skipReason: 'invalid_plan_management_kind',
      requiresAgencyCsvExport: false,
    };
  }

  let applied: NdisCategoryManagementKind | 'UNRESOLVED' = 'UNRESOLVED';

  if (planKind === 'MIXED') {
    const cat = String(input.supportCategory ?? '').trim();
    // Prefer category override when category present; otherwise honour default
    // (matches ndis_resolve_delivery_management_kind / mig 60+70).
    const override = cat
      ? (input.categoryOverrides ?? []).find((o) => String(o.supportCategory).trim() === cat)
      : undefined;
    const kind = String(override?.managementKind ?? input.defaultManagementKind ?? '').trim();
    if (!isNdisCategoryManagementKind(kind)) {
      return {
        payerKind: 'UNRESOLVED',
        managementKindApplied: 'UNRESOLVED',
        skipReason: 'mixed_plan_category_unresolved',
        requiresAgencyCsvExport: false,
      };
    }
    applied = kind;
  } else {
    applied = planKind as NdisCategoryManagementKind;
  }

  if (applied === 'PLAN_MANAGED') {
    return {
      payerKind: 'PLAN_MANAGER',
      managementKindApplied: applied,
      skipReason: null,
      requiresAgencyCsvExport: false,
    };
  }
  if (applied === 'SELF_MANAGED') {
    return {
      payerKind: 'CLIENT',
      managementKindApplied: applied,
      skipReason: null,
      requiresAgencyCsvExport: false,
    };
  }
  // AGENCY_MANAGED — NDIA claim via manual CSV export path
  return {
    payerKind: 'NDIA_AGENCY',
    managementKindApplied: applied,
    skipReason: null,
    requiresAgencyCsvExport: true,
  };
}

export type AgencyPaymentRequestCsvRow = {
  registrationNumber: string;
  ndisNumber: string;
  supportsDeliveredFrom: string;
  supportsDeliveredTo: string;
  supportNumber: string;
  claimReference: string;
  quantity: string;
  hours: string;
  unitPrice: string;
  gstCode: string;
  authorisedBy: string;
  participantApproved: string;
};

/** Build one bulk payment-request CSV body (myplace manual upload format subset). */
export function buildNdisAgencyBulkPaymentRequestCsv(
  rows: readonly AgencyPaymentRequestCsvRow[],
): string {
  const header = [
    'RegistrationNumber',
    'NDISNumber',
    'SupportsDeliveredFrom',
    'SupportsDeliveredTo',
    'SupportNumber',
    'ClaimReference',
    'Quantity',
    'Hours',
    'UnitPrice',
    'GSTCode',
    'AuthorisedBy',
    'ParticipantApproved',
  ];
  const escape = (v: string) => {
    const s = String(v ?? '');
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [header.join(',')];
  for (const r of rows) {
    lines.push(
      [
        r.registrationNumber,
        r.ndisNumber,
        r.supportsDeliveredFrom,
        r.supportsDeliveredTo,
        r.supportNumber,
        r.claimReference,
        r.quantity,
        r.hours,
        r.unitPrice,
        r.gstCode,
        r.authorisedBy,
        r.participantApproved,
      ]
        .map(escape)
        .join(','),
    );
  }
  return lines.join('\n') + '\n';
}
